import React, { useRef, useState } from "react";
import { Upload, X, ImagePlus, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const ACCEPT = "image/png,image/jpeg,image/webp,image/gif";
const MAX_MB = 5;

/**
 * Drag-and-drop / click-to-browse image uploader.
 * On success it calls onChange(url) with the public URL returned by the backend.
 *
 * Props:
 *   - value: string         (current image_url; "" if none)
 *   - onChange: (url) => void
 */
const ImageUploader = ({ value = "", onChange }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Image must be under ${MAX_MB}MB`);
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onInputChange = (e) => handleFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    handleFile(e.dataTransfer.files?.[0]);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    if (!dragOver) setDragOver(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };
  const clear = (e) => {
    e?.stopPropagation();
    onChange("");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="space-y-3">
      {/* Drop zone / preview */}
      <button
        type="button"
        onClick={() => !busy && inputRef.current?.click()}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        data-testid="admin-form-image-uploader"
        className={`relative w-full aspect-[3/4] max-w-[200px] border-2 border-dashed flex items-center justify-center text-center overflow-hidden transition-colors ${
          dragOver ? "border-[#4A3FCF] bg-[#EEEFFB]" : "border-[#DCE4F2] bg-white hover:border-[#4A3FCF]"
        } ${busy ? "cursor-wait opacity-70" : "cursor-pointer"}`}
      >
        {value ? (
          <>
            <img
              src={value}
              alt="Product preview"
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <span
              role="button"
              tabIndex={0}
              onClick={clear}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && clear(e)}
              data-testid="admin-form-image-clear"
              className="absolute top-1.5 right-1.5 inline-flex items-center justify-center bg-white/90 hover:bg-white text-[#0F1245] w-7 h-7 shadow-md z-10 cursor-pointer"
              aria-label="Remove image"
            >
              <X size={14} strokeWidth={2} />
            </span>
            {busy && (
              <div className="absolute inset-0 bg-[#0F1245]/40 flex items-center justify-center">
                <Loader2 size={22} className="text-white animate-spin" />
              </div>
            )}
          </>
        ) : busy ? (
          <div className="flex flex-col items-center gap-2 text-[#4A3FCF]">
            <Loader2 size={24} className="animate-spin" />
            <p className="text-[11px] tracking-[0.22em] uppercase">Uploading…</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 px-4 py-6 text-[#5A5F7A]">
            <ImagePlus size={26} strokeWidth={1.4} />
            <p className="text-[11px] tracking-[0.22em] uppercase leading-relaxed">
              Drop image<br />or click
            </p>
            <p className="text-[10px] text-[#9AA0B8]">PNG · JPG · WEBP · ≤{MAX_MB}MB</p>
          </div>
        )}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={onInputChange}
        data-testid="admin-form-image-input"
      />

      {/* Optional manual URL entry — handy for external/curated images */}
      <div>
        <input
          data-testid="admin-form-image"
          className="line-input text-[12px]"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="…or paste an https:// URL"
        />
      </div>
    </div>
  );
};

export default ImageUploader;
